import React from 'react';
import { Box, Typography, Button, Divider } from '@mui/material';
import TerminalIcon from '@mui/icons-material/Terminal';
import AnimationIcon from '@mui/icons-material/Animation';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { useThemeContext } from '../../context/ThemeContext';

export const Hero = () => {
    const { mode } = useThemeContext();
    const isLight = mode === 'light';

    return (
        <Box sx={{ mb: 8, textAlign: 'center', position: 'relative' }}>
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 3,
                    px: 2.5,
                    py: 0.75,
                    borderRadius: '20px',
                    border: isLight ? '1px solid rgba(59, 130, 246, 0.4)' : '1px solid rgba(59, 130, 246, 0.3)',
                    background: isLight ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
                    animation: 'pulseGlow 3s infinite alternate',
                    '@keyframes pulseGlow': {
                        '0%': { boxShadow: '0 0 0 0 rgba(59, 130, 246, 0.4)' },
                        '100%': { boxShadow: '0 0 20px 0 rgba(59, 130, 246, 0.1)' }
                    }
                }}
            >
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3b82f6', boxShadow: '0 0 10px #3b82f6' }} />
                <Typography variant="caption" sx={{ color: isLight ? '#2563eb' : '#60a5fa', fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase' }}>
                    Select Your Learning Track
                </Typography>
            </Box>

            <Typography
                variant="h1"
                fontWeight="800"
                sx={{
                    fontSize: { xs: '2.2rem', md: '3.5rem', xl: '4.5rem' },
                    letterSpacing: '-1.5px',
                    lineHeight: 1.1,
                    mb: 3,
                    background: isLight ? 'linear-gradient(to right, #1e293b 20%, #3b82f6 50%, #2563eb 80%)' : 'linear-gradient(to right, #f8fafc 20%, #93c5fd 50%, #60a5fa 80%)',
                    backgroundSize: '200% auto',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    animation: 'shine 5s linear infinite',
                    '@keyframes shine': { 'to': { backgroundPosition: '200% center' } }
                }}
            >
                Choose Your Path.
            </Typography>

            <Typography
                variant="subtitle1"
                sx={{
                    color: isLight ? '#64748b' : '#94a3b8',
                    fontWeight: 500,
                    maxWidth: { xs: '700px', xl: '850px' },
                    mx: 'auto',
                    lineHeight: 1.6,
                    fontSize: { xs: '1rem', md: '1.1rem', xl: '1.25rem' },
                    mb: 7
                }}
            >
                Stop memorizing. Start visualizing. Experience system design and algorithms through interactive, multi-frame animations that build true engineering intuition.
            </Typography>

            {/* CTA Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 7 }}>
                <Button
                    variant="contained"
                    size="large"
                    onClick={() => document.getElementById('catalog-section')?.scrollIntoView({ behavior: 'smooth' })}
                    sx={{
                        py: { xs: 2, xl: 2.5 }, px: { xs: 4, xl: 6 }, borderRadius: 2, fontWeight: 700, fontSize: '1.1rem',
                        textTransform: 'none', bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' }
                    }}
                >
                    Start Your Journey
                </Button>
                <Button
                    variant="outlined"
                    size="large"
                    onClick={() => document.getElementById('catalog-section')?.scrollIntoView({ behavior: 'smooth' })}
                    sx={{
                        py: { xs: 2, xl: 2.5 }, px: { xs: 4, xl: 6 }, borderRadius: 2, fontWeight: 700, fontSize: '1.1rem',
                        textTransform: 'none', borderColor: isLight ? '#e2e8f0' : '#1f1f22',
                        color: isLight ? '#0f172a' : '#f8fafc',
                        '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.05)', borderColor: '#3b82f6' }
                    }}
                >
                    Explore Catalog
                </Button>
            </Box>

            {/* Metrics Pill */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 10, px: 2 }}>
                <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: { xs: 3, md: 5, xl: 8 },
                    p: { xs: 3, md: 2, xl: 3 },
                    px: { md: 5, xl: 8 },
                    background: isLight ? 'rgba(255, 255, 255, 0.7)' : 'rgba(20, 20, 24, 0.7)',
                    backdropFilter: 'blur(20px)',
                    border: isLight ? '1px solid rgba(59, 130, 246, 0.15)' : '1px solid rgba(59, 130, 246, 0.1)',
                    borderRadius: { xs: '24px', md: '100px' },
                    boxShadow: isLight ? '0 10px 40px -10px rgba(59, 130, 246, 0.1)' : '0 10px 40px -10px rgba(0, 0, 0, 0.5)',
                    flexDirection: { xs: 'column', md: 'row' }
                }}>
                    <MetricItem icon={<TerminalIcon />} value="5,000+" label="Engineers Upgraded" isLight={isLight} color="#3b82f6" />
                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, borderColor: isLight ? '#e2e8f0' : '#27272a' }} />
                    <MetricItem icon={<AnimationIcon />} value="850+" label="Interactive Animations" isLight={isLight} color="#10b981" />
                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, borderColor: isLight ? '#e2e8f0' : '#27272a' }} />
                    <MetricItem icon={<WorkspacePremiumIcon />} value="100% FAANG" label="Interview Grade Depth" isLight={isLight} color="#f59e0b" />
                </Box>
            </Box>

            {/* Social Proof */}
            <Box sx={{ borderTop: isLight ? '1px solid #e2e8f0' : '1px solid #27272a', pt: 6, maxWidth: { xs: '800px', xl: '1100px' }, mx: 'auto' }}>
                <Typography variant="body2" sx={{ color: isLight ? '#94a3b8' : '#52525b', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', mb: 4, fontSize: { xl: '0.9rem' } }}>
                    Trusted by engineers from
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 3, md: 6, xl: 10 }, flexWrap: 'wrap' }}>
                    {['GOOGLE', 'META', 'AMAZON', 'NETFLIX', 'UBER'].map(company => (
                        <Typography key={company} variant="h6" sx={{ color: isLight ? '#cbd5e1' : '#3f3f46', fontWeight: 900, userSelect: 'none', fontSize: { xl: '1.5rem' } }}>
                            {company}
                        </Typography>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

const MetricItem = ({ icon, value, label, isLight, color }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {React.cloneElement(icon, { sx: { color, fontSize: 26 } })}
        <Box sx={{ textAlign: 'left' }}>
            <Typography sx={{ fontSize: '1.2rem', fontWeight: 900, color: isLight ? '#1e293b' : '#f8fafc', lineHeight: 1.1 }}>{value}</Typography>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: isLight ? '#64748b' : '#94a3b8' }}>{label}</Typography>
        </Box>
    </Box>
);
